import React from 'react';
import { Card, Table, Placeholder, Badge, Button } from 'react-bootstrap';
import { IconAlertTriangle, IconTool } from '@tabler/icons-react';
import useDashboardIncompleteBookings from '../../../services/dashboard-v2/hooks/useDashboardIncompleteBookings';
import { IncompleteBookingItem } from '../../../services/dashboard-v2/schemas/dashboardV2Schemas';

const cardStyle = {
    border: '1px solid #EAEAEA',
    boxShadow: 'none',
    borderRadius: '8px'
};

interface NeedsAttentionWidgetProps {
    propertyId: string;
    onFixIt: (bookingId: string) => void;
    onViewAll: () => void;
}

const MissingBadges: React.FC<{ item: IncompleteBookingItem }> = ({ item }) => (
    <div className="d-flex gap-1 flex-wrap">
        {!item.room_type_id && <Badge bg="" style={{ backgroundColor: '#FBE6E6', color: '#D63939', fontSize: '9px', fontWeight: 700 }}>NO ROOM TYPE</Badge>}
        {!item.rate_plan_id && <Badge bg="" style={{ backgroundColor: '#FBF1DE', color: '#C07A1E', fontSize: '9px', fontWeight: 700 }}>NO RATE PLAN</Badge>}
        {!item.room_id && <Badge bg="" style={{ backgroundColor: '#E6EDF9', color: '#3468B0', fontSize: '9px', fontWeight: 700 }}>NO ROOM</Badge>}
    </div>
);

const NeedsAttentionWidget: React.FC<NeedsAttentionWidgetProps> = ({ propertyId, onFixIt, onViewAll }) => {
    const { data, isLoading } = useDashboardIncompleteBookings({ property_id: propertyId, type: 'all', page: 1, per_page: 6 });

    const summary = data?.data.summary_counts;
    const items = data?.data.list.items ?? [];
    const total = data?.data.list.meta.total ?? 0;

    // Widget cuma tampil kalau memang ada reservasi yang perlu ditindaklanjuti --
    // hindari card kosong yang cuma menambah noise di dashboard.
    if (!isLoading && total === 0) {
        return null;
    }

    return (
        <Card style={cardStyle} className="mt-2 mb-3">
            <Card.Body className="p-0">
                <div className="d-flex align-items-center justify-content-between px-4 pt-3 pb-2">
                    <div className="d-flex align-items-center gap-2">
                        <div className="rounded-2 d-flex align-items-center justify-content-center" style={{ width: 26, height: 26, backgroundColor: '#FBF1DE', color: '#C07A1E' }}>
                            <IconAlertTriangle size={14} />
                        </div>
                        <span className="fw-bold" style={{ fontSize: '13.5px' }}>Needs Attention</span>
                    </div>
                    {!isLoading && (
                        <span className="fw-bold" style={{ backgroundColor: '#FBF1DE', color: '#C07A1E', fontSize: '11px', padding: '2px 9px', borderRadius: '999px' }}>{total}</span>
                    )}
                </div>

                {!isLoading && summary && (
                    <div className="d-flex gap-2 flex-wrap px-4 pb-3">
                        <span className="border rounded-2 px-2 py-1" style={{ fontSize: '11px', color: '#6c757d' }}><span className="rounded-circle d-inline-block me-1" style={{ width: 6, height: 6, backgroundColor: '#D63939' }}></span>{summary.missing_room_type} no room type</span>
                        <span className="border rounded-2 px-2 py-1" style={{ fontSize: '11px', color: '#6c757d' }}><span className="rounded-circle d-inline-block me-1" style={{ width: 6, height: 6, backgroundColor: '#C07A1E' }}></span>{summary.missing_rate_plan} no rate plan</span>
                        <span className="border rounded-2 px-2 py-1" style={{ fontSize: '11px', color: '#6c757d' }}><span className="rounded-circle d-inline-block me-1" style={{ width: 6, height: 6, backgroundColor: '#3468B0' }}></span>{summary.missing_room} no room</span>
                    </div>
                )}

                {isLoading ? (
                    <Placeholder as="div" animation="glow" className="p-4">
                        <Placeholder xs={12} size="lg" className="mb-3" />
                        <Placeholder xs={12} className="mb-2" />
                        <Placeholder xs={12} className="mb-2" />
                    </Placeholder>
                ) : (
                    <Table borderless responsive style={{ fontSize: '11px', margin: 0 }}>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.booking_id} style={{ borderTop: '1px solid #F8F9FA' }}>
                                    <td className="py-2 px-4" style={{ width: '30%' }}>
                                        <div className="fw-bold text-dark">{item.guest_name}</div>
                                        <div className="text-muted" style={{ fontSize: '10.5px' }}>{item.invoice_number || '-'} &middot; {item.check_in} &ndash; {item.check_out} &middot; {item.source || '-'}</div>
                                    </td>
                                    <td className="py-2 px-2">
                                        <MissingBadges item={item} />
                                    </td>
                                    <td className="py-2 px-4 text-end" style={{ width: 1 }}>
                                        <Button
                                            size="sm"
                                            style={{ backgroundColor: '#20C997', border: 'none', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}
                                            onClick={() => onFixIt(item.booking_id)}
                                        >
                                            <IconTool size={12} className="me-1" />
                                            Fix it
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                {!isLoading && total > items.length && (
                    <div className="text-center py-2 border-top">
                        <Button variant="link" size="sm" style={{ color: '#20C997', fontWeight: 700, fontSize: '12px', textDecoration: 'none' }} onClick={onViewAll}>
                            View all {total} reservations &rarr;
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default NeedsAttentionWidget;
